import React, { useEffect, useState, useCallback } from "react";
import {
  adminCreateVouchers,
  adminListVouchers,
  adminRevokeVoucher,
  adminDeleteVoucher,
  adminVoucherAttribution,
} from "../../lib/api";

const PLAN_LABEL = { pro: "PRO Player", elite: "PRO League" };

// ---------------------------------------------------------------------------
// Attribution rollup — "PRO partner manager". Shows redemptions / est-revenue
// grouped by prefix or notes so admins can see which influencer / campaign
// converted best. Drill into the row to see sample codes.
// ---------------------------------------------------------------------------
function AttributionView({ onToast }) {
  const [groupBy, setGroupBy] = useState("prefix");
  const [since, setSince] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (since) params.since = new Date(since).toISOString();
      const d = await adminVoucherAttribution(params);
      setData(d);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not load attribution.");
    } finally {
      setLoading(false);
    }
  }, [groupBy, since, onToast]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const usd = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div data-testid="voucher-attribution-view" className="space-y-5">
      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "REDEMPTIONS", value: totals.redemptions_count || 0, testid: "attr-total-redemptions" },
          { label: "UNIQUE REDEEMERS", value: totals.unique_redeemers || 0, testid: "attr-total-redeemers" },
          { label: "PRO DAYS GRANTED", value: totals.total_pro_days_granted || 0, testid: "attr-total-days" },
          { label: "EST. REVENUE", value: usd(totals.est_revenue_usd), testid: "attr-total-revenue" },
        ].map((s) => (
          <div key={s.label} data-testid={s.testid} className="rounded-2xl border border-[#14b8a6]/30 bg-[#14b8a6]/5 p-4">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6]">{s.label}</p>
            <p className="text-2xl font-display text-white mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">GROUP BY</label>
          <div className="flex gap-1" data-testid="attr-group-toggle">
            {[
              { id: "prefix", label: "PREFIX" },
              { id: "notes",  label: "NOTES" },
            ].map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setGroupBy(g.id)}
                data-testid={`attr-group-${g.id}`}
                className={`text-[10px] tracking-widest px-3 py-2 rounded-lg ${groupBy === g.id ? "btn-neon" : "btn-ghost"}`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">SINCE (OPTIONAL)</label>
          <input
            data-testid="attr-since-input"
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
          />
        </div>
        {since && (
          <button type="button" onClick={() => setSince("")} className="btn-ghost rounded-xl px-3 py-2 text-xs">
            CLEAR
          </button>
        )}
      </div>

      {/* Campaign table */}
      <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
        {loading && <p className="text-sm text-white/40">Loading…</p>}
        {!loading && (!data?.campaigns?.length) && (
          <p className="text-sm text-white/40 py-6 text-center">
            No vouchers minted yet. Mint your first batch with a prefix or notes to
            light up the attribution rollup.
          </p>
        )}
        {!loading && data?.campaigns?.length > 0 && (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="attr-campaign-table">
              <thead className="text-[10px] tracking-widest text-white/40">
                <tr>
                  <th className="text-left px-2 py-2">CAMPAIGN</th>
                  <th className="text-right px-2 py-2">CODES</th>
                  <th className="text-right px-2 py-2">REDEMPTIONS</th>
                  <th className="text-right px-2 py-2">UNIQUE</th>
                  <th className="text-right px-2 py-2">PRO / ELITE</th>
                  <th className="text-right px-2 py-2">DAYS</th>
                  <th className="text-right px-2 py-2">EST. REVENUE</th>
                  <th className="text-left px-2 py-2">SAMPLE CODES</th>
                </tr>
              </thead>
              <tbody className="text-white/90">
                {data.campaigns.map((c) => {
                  const conv = c.codes_count
                    ? Math.round((c.redemptions_count / c.codes_count) * 100)
                    : 0;
                  const safeId = (c.campaign || "_").replace(/[^A-Za-z0-9_-]+/g, "_");
                  return (
                    <tr key={c.campaign} data-testid={`attr-row-${safeId}`} className="border-t border-white/5">
                      <td className="px-2 py-2">
                        <p className="font-mono text-[#14b8a6]">{c.campaign}</p>
                        <p className="text-[10px] text-white/40">{conv}% redeemed</p>
                      </td>
                      <td className="px-2 py-2 text-right">{c.codes_count}</td>
                      <td className="px-2 py-2 text-right font-bold">{c.redemptions_count}</td>
                      <td className="px-2 py-2 text-right">{c.unique_redeemers}</td>
                      <td className="px-2 py-2 text-right text-[11px]">
                        <span className="text-[#14b8a6]">{c.plan_breakdown?.pro || 0}</span>
                        <span className="text-white/30 mx-1">/</span>
                        <span className="text-amber-300">{c.plan_breakdown?.elite || 0}</span>
                      </td>
                      <td className="px-2 py-2 text-right">{c.total_pro_days_granted}</td>
                      <td className="px-2 py-2 text-right font-mono text-green-400">{usd(c.est_revenue_usd)}</td>
                      <td className="px-2 py-2">
                        <p className="text-[10px] font-mono text-white/50 truncate max-w-[200px]">
                          {(c.sample_codes || []).slice(0, 3).join(", ")}
                          {c.codes_count > 3 && <span className="text-white/30"> …</span>}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-[10px] text-white/40 leading-relaxed">
          Revenue is an <span className="text-[#14b8a6]">estimate</span> based on retail
          PRO ($1.99/mo) and PRO League ($9.99/mo) prices × days granted. Treat it as
          a partnerships KPI, not accounting truth.
        </p>
      </div>
    </div>
  );
}


/**
 * Admin → Vouchers section.
 *
 * Lets the operator mint single or batched PRO/elite voucher codes,
 * optionally with a custom code, batch prefix, expiry, and shared
 * `max_redemptions`. Lists existing vouchers with redemption status,
 * supports revoke + hard-delete (when 0 redemptions). Includes an
 * Attribution view that rolls redemptions up by prefix / notes for
 * partner / influencer reporting.
 */
export function VouchersSection({ onToast }) {
  const [view, setView] = useState("manage");
  // ---- create form state ----
  const [customCode, setCustomCode] = useState("");
  const [plan, setPlan] = useState("pro");
  const [durationDays, setDurationDays] = useState(30);
  const [count, setCount] = useState(1);
  const [maxRedemptions, setMaxRedemptions] = useState(1);
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [prefix, setPrefix] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdBatch, setCreatedBatch] = useState(null);

  // ---- list state ----
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      const data = await adminListVouchers(params);
      setRows(data.vouchers || []);
      setTotal(data.total || 0);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not load vouchers.");
    } finally {
      setLoading(false);
    }
  }, [status, onToast]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault?.();
    setCreating(true);
    setCreatedBatch(null);
    try {
      const body = {
        plan,
        duration_days: Number(durationDays),
        count: Number(count),
        max_redemptions: Number(maxRedemptions),
        notes,
      };
      if (customCode.trim()) body.custom_code = customCode.trim().toUpperCase();
      if (prefix.trim()) body.prefix = prefix.trim().toUpperCase();
      if (expiresAt) {
        // <input type="datetime-local"> returns local time without TZ;
        // appending Z is wrong — let the user's browser send local + we
        // let the server interpret it as the requested instant. Trick:
        // construct a Date so JS adds the local TZ offset.
        body.expires_at = new Date(expiresAt).toISOString();
      }
      const res = await adminCreateVouchers(body);
      setCreatedBatch(res.vouchers || []);
      onToast?.(`Created ${res.count} voucher${res.count === 1 ? "" : "s"}.`);
      setCustomCode("");
      setNotes("");
      load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not create voucher.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (code) => {
    if (!window.confirm(`Revoke ${code}? This cannot be undone.`)) return;
    try {
      await adminRevokeVoucher(code);
      onToast?.(`Revoked ${code}.`);
      load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Revoke failed.");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Permanently delete ${code}?`)) return;
    try {
      await adminDeleteVoucher(code);
      onToast?.(`Deleted ${code}.`);
      load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      onToast?.(`Copied ${code} to clipboard.`);
    } catch {
      // ignore
    }
  };

  const copyAll = async () => {
    if (!createdBatch?.length) return;
    await navigator.clipboard.writeText(createdBatch.map((v) => v.code).join("\n"));
    onToast?.(`Copied ${createdBatch.length} codes.`);
  };

  return (
    <div data-testid="admin-vouchers-section" className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2" data-testid="voucher-view-toggle">
        <button
          type="button"
          onClick={() => setView("manage")}
          data-testid="voucher-view-manage"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${view === "manage" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-ticket mr-1" />MANAGE
        </button>
        <button
          type="button"
          onClick={() => setView("attribution")}
          data-testid="voucher-view-attribution"
          className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${view === "attribution" ? "btn-neon" : "btn-ghost"}`}
        >
          <i className="fas fa-chart-line mr-1" />ATTRIBUTION
        </button>
      </div>

      {view === "attribution" ? (
        <AttributionView onToast={onToast} />
      ) : (
        <>
      {/* CREATE FORM */}
      <form
        onSubmit={create}
        data-testid="voucher-create-form"
        className="rounded-2xl border border-[#14b8a6]/30 bg-black/40 p-5 space-y-4"
      >
        <h3 className="text-lg font-display grad-text">
          <i className="fas fa-ticket mr-2" />MINT VOUCHERS
        </h3>
        <p className="text-[11px] text-white/50">
          Generate single-use codes that unlock PRO for the redeemer.
          Set a custom code, or leave blank for cryptographically random
          codes. Use the prefix to brand batches (e.g. <span className="text-[#14b8a6]">LAUNCH-</span>).
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">PLAN</label>
            <select
              data-testid="voucher-plan-select"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            >
              <option value="pro">PRO Player — $1.99/mo perks</option>
              <option value="elite">PRO League — $9.99/mo perks (elite)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">DURATION (DAYS)</label>
            <input
              data-testid="voucher-duration-input"
              type="number" min="1" max="3650"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">HOW MANY CODES?</label>
            <input
              data-testid="voucher-count-input"
              type="number" min="1" max="500"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              disabled={!!customCode.trim()}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white disabled:opacity-50"
            />
            {customCode.trim() && (
              <p className="text-[10px] text-white/40 mt-1">Locked at 1 with custom code.</p>
            )}
          </div>
          <div>
            <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">MAX REDEMPTIONS PER CODE</label>
            <input
              data-testid="voucher-max-redemptions-input"
              type="number" min="1" max="10000"
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            />
            <p className="text-[10px] text-white/40 mt-1">1 = single-use. &gt;1 lets the same code work for multiple people.</p>
          </div>
          <div>
            <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">EXPIRES AT (OPTIONAL)</label>
            <input
              data-testid="voucher-expires-input"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">PREFIX (OPTIONAL)</label>
            <input
              data-testid="voucher-prefix-input"
              type="text"
              placeholder="e.g. LAUNCH"
              maxLength={16}
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              disabled={!!customCode.trim()}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white uppercase disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">CUSTOM CODE (OPTIONAL · OVERRIDES BATCH)</label>
          <input
            data-testid="voucher-custom-code-input"
            type="text"
            placeholder="e.g. WELCOME30"
            maxLength={32}
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white uppercase font-mono tracking-wider"
          />
        </div>

        <div>
          <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">NOTES (ADMIN-ONLY)</label>
          <input
            data-testid="voucher-notes-input"
            type="text"
            placeholder="e.g. influencer drop, 5 codes for X"
            maxLength={200}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
          />
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <button
            type="submit"
            data-testid="voucher-create-submit"
            disabled={creating}
            className="btn-neon rounded-xl px-5 py-2.5 font-bold tracking-widest disabled:opacity-50"
          >
            {creating ? "GENERATING…" : "+ GENERATE"}
          </button>
          {createdBatch?.length > 0 && (
            <button
              type="button"
              onClick={copyAll}
              data-testid="voucher-copy-all-btn"
              className="btn-ghost rounded-xl px-4 py-2 text-sm"
            >
              <i className="fas fa-copy mr-1" />COPY ALL ({createdBatch.length})
            </button>
          )}
        </div>

        {createdBatch?.length > 0 && (
          <div
            data-testid="voucher-created-batch"
            className="mt-3 rounded-xl border border-[#14b8a6]/40 bg-[#14b8a6]/5 p-3 max-h-60 overflow-y-auto"
          >
            <p className="text-[10px] tracking-widest text-[#14b8a6] mb-2">
              JUST CREATED — copy these before they scroll away
            </p>
            <ul className="space-y-1 font-mono text-sm">
              {createdBatch.map((v) => (
                <li key={v.code} className="flex items-center justify-between gap-2">
                  <span className="text-white">{v.code}</span>
                  <button
                    type="button"
                    onClick={() => copyCode(v.code)}
                    className="text-[10px] text-[#14b8a6] hover:underline"
                    data-testid={`voucher-created-copy-${v.code}`}
                  >
                    COPY
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>

      {/* LIST */}
      <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-lg font-display grad-text">
            <i className="fas fa-list mr-2" />ALL VOUCHERS · {total}
          </h3>
          <div className="flex gap-1 flex-wrap" data-testid="voucher-status-filters">
            {[
              { id: "all", label: "ALL" },
              { id: "active", label: "ACTIVE" },
              { id: "redeemed", label: "REDEEMED" },
              { id: "expired", label: "EXPIRED" },
              { id: "revoked", label: "REVOKED" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatus(s.id)}
                data-testid={`voucher-filter-${s.id}`}
                className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg ${status === s.id ? "btn-neon" : "btn-ghost"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-white/40">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-white/40 py-6 text-center">No vouchers match this filter.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-widest text-white/40">
                <tr>
                  <th className="text-left px-2 py-2">CODE</th>
                  <th className="text-left px-2 py-2">PLAN</th>
                  <th className="text-left px-2 py-2">DURATION</th>
                  <th className="text-left px-2 py-2">REDEEMED</th>
                  <th className="text-left px-2 py-2">EXPIRES</th>
                  <th className="text-left px-2 py-2">NOTES</th>
                  <th className="text-right px-2 py-2">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="text-white/90">
                {rows.map((v) => {
                  const used = (v.redeemed_count || 0) >= (v.max_redemptions || 1);
                  const expired = v.expires_at && new Date(v.expires_at) < new Date();
                  return (
                    <tr key={v.code} data-testid={`voucher-row-${v.code}`} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-2 py-2 font-mono">{v.code}</td>
                      <td className="px-2 py-2 text-[#14b8a6]">{PLAN_LABEL[v.plan] || v.plan}</td>
                      <td className="px-2 py-2">{v.duration_days}d</td>
                      <td className="px-2 py-2">
                        <span className={used ? "text-amber-300" : "text-white/80"}>
                          {v.redeemed_count || 0} / {v.max_redemptions || 1}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-white/60">
                        {v.expires_at ? new Date(v.expires_at).toLocaleDateString() : "never"}
                        {expired && <span className="text-red-400 ml-1">· EXPIRED</span>}
                        {v.revoked && <span className="text-red-400 ml-1">· REVOKED</span>}
                      </td>
                      <td className="px-2 py-2 text-xs text-white/60 truncate max-w-[160px]">{v.notes || "—"}</td>
                      <td className="px-2 py-2 text-right space-x-2">
                        <button
                          type="button"
                          onClick={() => copyCode(v.code)}
                          className="text-[10px] text-[#14b8a6] hover:underline"
                          data-testid={`voucher-row-copy-${v.code}`}
                        >
                          COPY
                        </button>
                        {!v.revoked && (
                          <button
                            type="button"
                            onClick={() => revoke(v.code)}
                            className="text-[10px] text-amber-300 hover:underline"
                            data-testid={`voucher-row-revoke-${v.code}`}
                          >
                            REVOKE
                          </button>
                        )}
                        {(v.redeemed_count || 0) === 0 && (
                          <button
                            type="button"
                            onClick={() => remove(v.code)}
                            className="text-[10px] text-red-400 hover:underline"
                            data-testid={`voucher-row-delete-${v.code}`}
                          >
                            DELETE
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}

export default VouchersSection;
